import { LogOut, Menu, MessageSquarePlus } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useNavigation } from "react-router";

import { FeedbackDialog } from "@/components/FeedbackDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { authClient } from "@/auth/auth-client";
import { useWhoami } from "@/auth/use-whoami";
import { LocalRoleSwitcher } from "./LocalRoleSwitcher";
import { CommandPalette } from "./CommandPalette";
import { RoleBadge } from "./RoleBadge";
import { SidebarNav } from "./SidebarNav";
import { UserSearchBox, UserSearchIconButton } from "./UserSearchBox";

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <span className="bg-primary size-2 rounded-full" aria-hidden />
      <span className="text-base font-semibold tracking-tight">Moreta</span>
      <span className="text-muted-foreground font-mono text-[11px] tracking-wide uppercase">
        ops
      </span>
      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] uppercase">
        Beta
      </Badge>
      {authClient.mode === "local" && <Badge variant="outline">local</Badge>}
    </div>
  );
}

function Sidebar({ onFeedback }: { onFeedback: () => void }) {
  return (
    <aside className="sticky top-0 hidden h-svh w-56 shrink-0 flex-col border-r bg-sidebar md:flex">
      <div className="flex h-14 items-center border-b px-4">
        <Brand />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <SidebarNav />
      </div>
      <div className="border-t p-2">
        <Button
          variant="ghost"
          className="text-muted-foreground w-full justify-start"
          onClick={onFeedback}
        >
          <MessageSquarePlus className="size-4" />
          Send feedback
        </Button>
      </div>
    </aside>
  );
}

/** Below md the sidebar folds into a left drawer behind this button. */
function MobileNav() {
  const [open, setOpen] = useState(false);

  // The trigger hides at md, but an already-open drawer would survive the
  // breakpoint (portal, overlay, focus trap, scroll lock) — e.g. a phone
  // rotating to landscape — leaving the page inert behind it. Close it as
  // soon as the desktop sidebar takes over.
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (mql.matches) setOpen(false);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open navigation"
          className="md:hidden"
        >
          <Menu className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-64 gap-0 p-0"
        aria-describedby={undefined}
      >
        <SheetHeader className="h-14 shrink-0 justify-center border-b px-4 py-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Brand />
        </SheetHeader>
        {/* Same scroll container as the desktop rail: SheetContent is a flex
            column with p-0, so a nav taller than the drawer overflows instead
            of scrolling — already tight on a small phone, and more so with
            group headers. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SidebarNav onNavigate={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Header({
  onFeedback,
  onSearch,
}: {
  onFeedback: () => void;
  onSearch: () => void;
}) {
  const { data: whoami } = useWhoami();

  return (
    <header className="flex h-14 items-center gap-2 border-b px-4 md:px-6">
      <MobileNav />
      <div className="md:hidden">
        <Brand />
      </div>
      {/* Global search — opens the ⌘K palette; renders only for USERS_READ. */}
      <div className="hidden min-w-0 flex-1 justify-center md:flex">
        <div className="w-full max-w-md">
          <UserSearchBox onOpen={onSearch} />
        </div>
      </div>
      <div className="ml-auto flex min-w-0 items-center gap-3">
        <UserSearchIconButton onOpen={onSearch} />
        <Button
          variant="ghost"
          size="icon"
          aria-label="Send feedback"
          title="Send feedback"
          className="md:hidden"
          onClick={onFeedback}
        >
          <MessageSquarePlus className="size-4" />
        </Button>
        {whoami && (
          <>
            <span className="text-muted-foreground hidden truncate text-sm sm:inline">
              {whoami.email}
            </span>
            {/* Also hidden on phones — the top bar has no room left there. */}
            <div className="hidden sm:block">
              <RoleBadge role={whoami.role} />
            </div>
          </>
        )}
        {/* Renders only under VITE_AUTH_MODE=local. Sits outside the whoami
            guard so a role whose whoami call is still in flight — or a role
            you switched into by mistake — can always be switched back.
            Hidden on phones: it overflows the top bar there, and role
            simulation is a desktop-dev activity. */}
        <div className="hidden sm:block">
          <LocalRoleSwitcher />
        </div>
        {authClient.mode === "msal" && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={() => void authClient.logout()}
          >
            <LogOut className="size-4" />
          </Button>
        )}
      </div>
    </header>
  );
}

export function AppShell() {
  const navigation = useNavigation();
  const navigating = navigation.state !== "idle";
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <div className="flex min-h-svh bg-background">
      <a
        href="#main-content"
        className="bg-background focus:ring-ring sr-only z-50 rounded-md p-3 focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:ring-2"
      >
        Skip to content
      </a>
      {navigating && (
        <div
          role="status"
          className="bg-primary fixed inset-x-0 top-0 z-50 h-1 animate-pulse motion-reduce:animate-none"
        >
          <span className="sr-only">Loading page…</span>
        </div>
      )}
      <Sidebar onFeedback={() => setFeedbackOpen(true)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          onFeedback={() => setFeedbackOpen(true)}
          onSearch={() => setPaletteOpen(true)}
        />
        {/* overflow-x-clip (not -hidden): clips horizontal overflow without
            making <main> a scroll container, so position:sticky inside a page
            (the Users header) still sticks to the viewport. */}
        <main
          id="main-content"
          tabIndex={-1}
          aria-busy={navigating}
          className="flex-1 overflow-x-clip p-4 outline-none md:p-6"
        >
          <Outlet />
        </main>
      </div>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      {/* One palette for the whole app — ⌘K anywhere, or the header box. */}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
